import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Badge,
  ButtonGroup,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  Checkbox,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2 } from 'react-icons/fi';
import { goalsApi } from '../services/api';

function GoalsManagement() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ name: '', is_active: true });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchGoals();
    // eslint-disable-next-line
  }, [showInactive]);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await goalsApi.getAll(showInactive);
      setGoals(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch goals');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (goal = null) => {
    if (goal) {
      setEditingGoal(goal);
      setGoalForm({ name: goal.name, is_active: goal.is_active });
    } else {
      setEditingGoal(null);
      setGoalForm({ name: '', is_active: true });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    setEditingGoal(null);
    setGoalForm({ name: '', is_active: true });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGoal) {
        await goalsApi.update(editingGoal.id, goalForm);
        toast({
          title: 'Goal updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await goalsApi.create(goalForm);
        toast({
          title: 'Goal created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      handleCloseModal();
      fetchGoals();
    } catch (err) {
      toast({
        title: editingGoal ? 'Failed to update goal' : 'Failed to create goal',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(err);
    }
  };

  const handleToggleActive = async (goal) => {
    try {
      if (goal.is_active) {
        await goalsApi.delete(goal.id);
        toast({
          title: 'Goal deactivated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await goalsApi.update(goal.id, {
          ...goal,
          is_active: true
        });
        toast({
          title: 'Goal reactivated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      fetchGoals();
    } catch (err) {
      toast({
        title: 'Failed to update goal status',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="xl" color="brand.purple" thickness="4px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Heading size="md" mb={2}>Client Goals</Heading>
          <Text color="gray.600" fontSize="sm">
            Manage aesthetic goals that clients can select during registration
          </Text>
        </Box>
        <Flex gap={4} align="center">
          <Checkbox
            isChecked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            colorScheme="purple"
          >
            Show Inactive
          </Checkbox>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="purple"
            onClick={() => handleOpenModal()}
          >
            Add New Goal
          </Button>
        </Flex>
      </Flex>

      {goals.length === 0 ? (
        <Text color="gray.500">No goals found.</Text>
      ) : (
        <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="sm">
          <Table variant="simple">
            <Thead bg="brand.darkPurple">
              <Tr>
                <Th color="white">ID</Th>
                <Th color="white">Goal Name</Th>
                <Th color="white">Status</Th>
                <Th color="white">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {goals.map((goal) => (
                <Tr
                  key={goal.id}
                  opacity={goal.is_active ? 1 : 0.5}
                  _hover={{ bg: 'brand.lightGray' }}
                >
                  <Td>{goal.id}</Td>
                  <Td fontWeight="medium">{goal.name}</Td>
                  <Td>
                    <Badge colorScheme={goal.is_active ? 'green' : 'red'}>
                      {goal.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    <ButtonGroup size="sm" spacing={2}>
                      <Button
                        leftIcon={<FiEdit2 />}
                        colorScheme="purple"
                        variant="outline"
                        onClick={() => handleOpenModal(goal)}
                      >
                        Edit
                      </Button>
                      <Button
                        colorScheme={goal.is_active ? 'red' : 'green'}
                        variant="outline"
                        onClick={() => handleToggleActive(goal)}
                      >
                        {goal.is_active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </ButtonGroup>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Goal Form Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingGoal ? 'Edit Goal' : 'Add New Goal'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormControl isRequired mb={4}>
                <FormLabel>Goal Name</FormLabel>
                <Input
                  value={goalForm.name}
                  onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                  placeholder="e.g., Brighten skin tone"
                />
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={goalForm.is_active}
                  onChange={(e) => setGoalForm({ ...goalForm, is_active: e.target.checked })}
                  colorScheme="purple"
                >
                  Active
                </Checkbox>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup spacing={3}>
                <Button variant="ghost" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button colorScheme="purple" type="submit">
                  {editingGoal ? 'Update' : 'Create'}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default GoalsManagement;
